import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import { Gift, Package } from 'lucide-react';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Switch } from '@/shared/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Wizard, type WizardStep } from '@/shared/components/wizard';
import type { Product, ProductRedemptionType } from '@/shared/types/product';
import { saveProduct } from '../api/use-products';

const schema = z.object({
  name: z.string().min(1, 'Informe o nome'),
  uniqueId: z.string().min(1, 'Informe o SKU'),
  value: z.coerce.number().min(0, 'Pontos inválidos'),
  redemption: z.boolean(),
  redemptionType: z.enum(['birthday', 'courtesy', 'discount', 'product']),
  price: z.string().optional(),
});

type FormValues = z.infer<typeof schema>;

interface ProductFormDialogProps {
  idPassbook: string;
  product: Product | null;
  /** Página de origem: define resgate e o tipo na criação. */
  mode: 'product' | 'redemption';
  /** `passbook.redeemType`, já normalizado. */
  redeemType: ProductRedemptionType;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved: () => void;
}

export function ProductFormDialog({
  idPassbook,
  product,
  mode,
  redeemType,
  open,
  onOpenChange,
  onSaved,
}: ProductFormDialogProps) {
  const isEdit = Boolean(product);
  const isRedemption = mode === 'redemption';
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: '',
      uniqueId: '',
      value: 0,
      redemption: false,
      redemptionType: 'product',
      price: '0',
    },
  });

  useEffect(() => {
    if (!open) return;
    form.reset(
      product
        ? {
            name: product.name,
            uniqueId: product.uniqueId,
            value: Number(product.value) || 0,
            redemption: Boolean(product.redemption),
            redemptionType: (product.redemptionType ?? 'product') as ProductRedemptionType,
            price: String(product.price ?? 0),
          }
        : {
            name: '',
            uniqueId: '',
            value: 0,
            redemption: isRedemption,
            redemptionType: redeemType,
            price: '0',
          },
    );
  }, [open, product, form, isRedemption, redeemType]);

  const errors = form.formState.errors;
  const redemption = form.watch('redemption');

  const onValid = form.handleSubmit(async (values) => {
    try {
      await saveProduct({
        action: isEdit ? 'edit' : 'insert',
        id: product?._id,
        idPassbook,
        uniqueId: values.uniqueId,
        name: values.name,
        value: values.value,
        redemption: isEdit ? values.redemption : isRedemption,
        redemptionType: isEdit ? values.redemptionType : redeemType,
        price: values.price ?? '0',
      });
      toast.success(
        isEdit
          ? isRedemption
            ? 'Resgate atualizado.'
            : 'Produto atualizado.'
          : isRedemption
            ? 'Resgate criado.'
            : 'Produto criado.',
      );
      onOpenChange(false);
      onSaved();
    } catch {
      toast.error('Falha ao salvar produto.');
    }
  });

  const detailStep: WizardStep = {
      id: 'details',
      title: 'Detalhes',
      icon: Package,
      validate: () => form.trigger(['name', 'uniqueId', 'value', 'price']),
      content: (
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="prodName">Nome</Label>
            <Input id="prodName" placeholder="Ex.: Café grátis" className="h-11" {...form.register('name')} />
            {errors.name ? <p className="text-xs text-destructive">{errors.name.message}</p> : null}
          </div>
          <div className="space-y-2">
            <Label htmlFor="prodSku">SKU</Label>
            <Input id="prodSku" readOnly={isEdit} className="h-11" {...form.register('uniqueId')} />
            {errors.uniqueId ? <p className="text-xs text-destructive">{errors.uniqueId.message}</p> : null}
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="prodValue">Valor (custo no saldo)</Label>
              <Input id="prodValue" type="number" step="0.01" className="h-11" {...form.register('value')} />
              {errors.value ? <p className="text-xs text-destructive">{errors.value.message}</p> : null}
              <p className="text-xs text-muted-foreground">Pontos ou R$ debitados do saldo no resgate.</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="prodPrice">Desconto em R$</Label>
              <Input id="prodPrice" className="h-11" {...form.register('price')} />
              <p className="text-xs text-muted-foreground">
                Usado no Operador quando o tipo é Desconto (abate na conta).
              </p>
            </div>
          </div>
        </div>
      ),
  };

  const redemptionStep: WizardStep = {
      id: 'redemption',
      title: 'Resgate',
      icon: Gift,
      content: (
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="prodType">Tipo de resgate</Label>
            <Controller
              name="redemptionType"
              control={form.control}
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger id="prodType" className="h-11">
                    <SelectValue placeholder="Selecione o tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="product">Produto</SelectItem>
                    <SelectItem value="discount">Desconto</SelectItem>
                    <SelectItem value="courtesy">Cortesia</SelectItem>
                    <SelectItem value="birthday">Aniversário</SelectItem>
                  </SelectContent>
                </Select>
              )}
            />
          </div>
          <div className="flex items-center justify-between rounded-xl border border-border bg-[hsl(var(--glass-bg)/0.4)] p-4">
            <div>
              <p className="text-sm font-medium">Permite resgate</p>
              <p className="text-xs text-muted-foreground">Disponível no app do cliente.</p>
            </div>
            <Switch checked={redemption} onCheckedChange={(v) => form.setValue('redemption', v)} />
          </div>
        </div>
      ),
  };

  const steps = isEdit ? [detailStep, redemptionStep] : [detailStep];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {isEdit ? (isRedemption ? 'Editar resgate' : 'Editar produto') : isRedemption ? 'Novo resgate' : 'Novo produto'}
          </DialogTitle>
          <DialogDescription>
            {isRedemption
              ? 'Benefício disponível para resgate. O tipo segue a regra do passbook.'
              : 'Produto do catálogo, sem resgate no app. O tipo segue a regra do passbook.'}
          </DialogDescription>
        </DialogHeader>
        <Wizard
          steps={steps}
          onFinish={onValid}
          onCancel={() => onOpenChange(false)}
          isSubmitting={form.formState.isSubmitting}
          finishLabel={isEdit ? 'Salvar' : isRedemption ? 'Criar resgate' : 'Criar produto'}
          mobileOptimized
        />
      </DialogContent>
    </Dialog>
  );
}
